import React from "react";

/*
 * Decorative hero illustrations — translucent white line/volume art that sits on
 * the right of an asf-hero (see AdminSurface Hero `art` prop + .asf-hero__art).
 * Same visual language as the Admin home hero: layered ellipses/paths at varying
 * opacity, floating accent dots and dashed connection lines. Purely decorative
 * (aria-hidden handled by the Hero wrapper). All ~210×140.
 */

const W = 210;
const H = 140;
const svgProps = { width: W, height: H, viewBox: `0 0 ${W} ${H}`, fill: "none" };

// Shared floating accent dots + dashed connectors, reused across motifs.
const Accents = () => (
  <>
    <circle cx="26" cy="24" r="4.5" fill="rgba(255,255,255,0.24)" />
    <circle cx="16" cy="58" r="3" fill="rgba(255,255,255,0.17)" />
    <circle cx="196" cy="96" r="3.5" fill="rgba(255,255,255,0.20)" />
    <circle cx="202" cy="40" r="2.5" fill="rgba(255,255,255,0.22)" />
    <circle cx="40" cy="112" r="3" fill="rgba(255,255,255,0.15)" />
    <line x1="26" y1="24" x2="52" y2="48" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="3 3" />
    <line x1="196" y1="96" x2="176" y2="80" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="3 3" />
  </>
);

// Stacked database cylinders + shield (databases / storage backends).
export const StackArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="120" cy="112" rx="58" ry="13" fill="rgba(255,255,255,0.08)" />
    <ellipse cx="136" cy="70" rx="34" ry="10" fill="rgba(255,255,255,0.14)" />
    <rect x="102" y="70" width="68" height="38" fill="rgba(255,255,255,0.10)" />
    <ellipse cx="136" cy="108" rx="34" ry="10" fill="rgba(255,255,255,0.13)" />
    <ellipse cx="114" cy="56" rx="26" ry="8" fill="rgba(255,255,255,0.20)" />
    <rect x="88" y="56" width="52" height="30" fill="rgba(255,255,255,0.15)" />
    <ellipse cx="114" cy="86" rx="26" ry="8" fill="rgba(255,255,255,0.18)" />
    <ellipse cx="96" cy="44" rx="18" ry="6" fill="rgba(255,255,255,0.30)" />
    <rect x="78" y="44" width="36" height="22" fill="rgba(255,255,255,0.22)" />
    <ellipse cx="96" cy="66" rx="18" ry="6" fill="rgba(255,255,255,0.27)" />
    <path d="M182 24 L198 30 L198 42 C198 51 182 57 182 57 C182 57 166 51 166 42 L166 30 Z"
      fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
    <polyline points="175,40 179,44 189,34"
      stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <Accents />
  </svg>
);

// Isometric stacked sheets (batches / document flow).
export const LayersArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <path d="M112 34 L162 60 L112 86 L62 60 Z" fill="rgba(255,255,255,0.30)" />
    <path d="M112 56 L162 82 L112 108 L62 82 Z" fill="rgba(255,255,255,0.18)" />
    <path d="M112 78 L162 104 L112 130 L62 104 Z" fill="rgba(255,255,255,0.10)" />
    <path d="M112 34 L162 60 L112 86 L62 60 Z" stroke="rgba(255,255,255,0.34)" strokeWidth="1.1" />
    <Accents />
  </svg>
);

// Activity pulse line with nodes (monitoring / real-time).
export const PulseArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="112" cy="120" rx="78" ry="10" fill="rgba(255,255,255,0.06)" />
    <polyline points="24,86 58,86 74,44 100,116 122,72 146,72 166,50 196,50"
      stroke="rgba(255,255,255,0.62)" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="74" cy="44" r="4.5" fill="rgba(255,255,255,0.9)" />
    <circle cx="100" cy="116" r="4.5" fill="rgba(255,255,255,0.55)" />
    <circle cx="166" cy="50" r="4.5" fill="rgba(255,255,255,0.9)" />
    <line x1="24" y1="66" x2="196" y2="66" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="2 5" />
    <Accents />
  </svg>
);

// Interlocking gears (processing configuration).
const Gear = ({ cx, cy, r, teeth = 8, tw = 7, th = 8, hole, opacity }) => {
  const spokes = [];
  for (let i = 0; i < teeth; i++) {
    const a = (i * 360) / teeth;
    spokes.push(
      <rect key={i} x={cx - tw / 2} y={cy - r - th} width={tw} height={th} rx="2"
        fill={`rgba(255,255,255,${opacity})`} transform={`rotate(${a} ${cx} ${cy})`} />
    );
  }
  return (
    <g>
      {spokes}
      <circle cx={cx} cy={cy} r={r} fill={`rgba(255,255,255,${opacity})`} />
      <circle cx={cx} cy={cy} r={hole} fill="rgba(49,46,129,0.55)" />
    </g>
  );
};
export const GearArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <Gear cx="100" cy="66" r="30" teeth={9} tw={9} th={11} hole={12} opacity={0.22} />
    <Gear cx="150" cy="98" r="19" teeth={8} tw={6} th={8} hole={7.5} opacity={0.3} />
    <Accents />
  </svg>
);

// Server racks with status lights (hosts / deployment targets).
export const ServerArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <rect x="66" y="34" width="94" height="24" rx="5" fill="rgba(255,255,255,0.26)" />
    <circle cx="80" cy="46" r="3.5" fill="rgba(255,255,255,0.85)" />
    <rect x="98" y="43" width="46" height="6" rx="3" fill="rgba(255,255,255,0.4)" />
    <rect x="66" y="64" width="94" height="24" rx="5" fill="rgba(255,255,255,0.18)" />
    <circle cx="80" cy="76" r="3.5" fill="rgba(255,255,255,0.7)" />
    <rect x="98" y="73" width="46" height="6" rx="3" fill="rgba(255,255,255,0.32)" />
    <rect x="66" y="94" width="94" height="24" rx="5" fill="rgba(255,255,255,0.11)" />
    <circle cx="80" cy="106" r="3.5" fill="rgba(255,255,255,0.5)" />
    <rect x="98" y="103" width="46" height="6" rx="3" fill="rgba(255,255,255,0.22)" />
    <Accents />
  </svg>
);

// Circular retention/recycle loop with a clock hint (lifecycle).
export const RecycleArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <path d="M112 34 A42 42 0 0 1 150 96" stroke="rgba(255,255,255,0.30)" strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M150 96 A42 42 0 0 1 74 96" stroke="rgba(255,255,255,0.20)" strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M74 96 A42 42 0 0 1 112 34" stroke="rgba(255,255,255,0.13)" strokeWidth="9" fill="none" strokeLinecap="round" />
    <polygon points="112,26 112,42 124,34" fill="rgba(255,255,255,0.5)" />
    <circle cx="112" cy="72" r="18" fill="rgba(255,255,255,0.14)" />
    <polyline points="112,62 112,72 120,77" stroke="rgba(255,255,255,0.8)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Accents />
  </svg>
);

// Cloud download with progress rings (system updates).
export const UpdateArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="112" cy="120" rx="56" ry="8" fill="rgba(255,255,255,0.06)" />
    <path d="M86 84 a18 18 0 0 1 3 -35 a24 24 0 0 1 46 6 a15 15 0 0 1 -3 29 Z"
      fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.3" />
    <line x1="112" y1="70" x2="112" y2="104" stroke="rgba(255,255,255,0.85)" strokeWidth="2.6" strokeLinecap="round" />
    <polyline points="101,95 112,106 123,95" stroke="rgba(255,255,255,0.85)" strokeWidth="2.6"
      fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M150 60 A44 44 0 0 0 74 60" stroke="rgba(255,255,255,0.16)" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 5" />
    <circle cx="150" cy="60" r="3.5" fill="rgba(255,255,255,0.5)" />
    <Accents />
  </svg>
);

// Certificate seal with checkmark + ribbon (license).
export const LicenseArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="112" cy="120" rx="58" ry="8" fill="rgba(255,255,255,0.06)" />
    <path d="M112 36 L150 50 L150 78 C150 100 112 114 112 114 C112 114 74 100 74 78 L74 50 Z"
      fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.4" />
    <circle cx="112" cy="72" r="20" fill="rgba(255,255,255,0.14)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.3" />
    <polyline points="103,72 110,79 123,64" stroke="rgba(255,255,255,0.9)" strokeWidth="2.6"
      fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M104 104 L104 128 L112 121 L120 128 L120 104 Z" fill="rgba(255,255,255,0.3)" />
    <Accents />
  </svg>
);

// Notification bell with signal rings (notifications).
export const BellArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="112" cy="118" rx="60" ry="9" fill="rgba(255,255,255,0.06)" />
    <path d="M92 96 C92 70 90 44 112 44 C134 44 132 70 132 96 Z" fill="rgba(255,255,255,0.22)" />
    <path d="M92 96 C92 70 90 44 112 44 C134 44 132 70 132 96 Z" stroke="rgba(255,255,255,0.4)" strokeWidth="1.4" />
    <line x1="112" y1="34" x2="112" y2="44" stroke="rgba(255,255,255,0.5)" strokeWidth="3" strokeLinecap="round" />
    <rect x="80" y="96" width="64" height="7" rx="3.5" fill="rgba(255,255,255,0.3)" />
    <path d="M104 108 C104 116 120 116 120 108" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="140" cy="46" r="9" fill="rgba(255,255,255,0.55)" />
    <path d="M150 60 A26 26 0 0 0 150 40" stroke="rgba(255,255,255,0.22)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M74 40 A26 26 0 0 0 74 60" stroke="rgba(255,255,255,0.16)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <Accents />
  </svg>
);

// People / member avatars with connection lines (users & groups).
export const UsersArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <ellipse cx="112" cy="120" rx="76" ry="9" fill="rgba(255,255,255,0.06)" />
    {/* back-left member */}
    <circle cx="72" cy="66" r="13" fill="rgba(255,255,255,0.16)" />
    <path d="M52 100 C52 86 92 86 92 100 Z" fill="rgba(255,255,255,0.13)" />
    {/* back-right member */}
    <circle cx="152" cy="66" r="13" fill="rgba(255,255,255,0.16)" />
    <path d="M132 100 C132 86 172 86 172 100 Z" fill="rgba(255,255,255,0.13)" />
    {/* front member */}
    <circle cx="112" cy="52" r="18" fill="rgba(255,255,255,0.30)" />
    <path d="M84 96 C84 76 140 76 140 96 Z" fill="rgba(255,255,255,0.24)" />
    <line x1="90" y1="60" x2="130" y2="60" stroke="rgba(255,255,255,0.14)" strokeWidth="1" strokeDasharray="3 3" />
    <Accents />
  </svg>
);

// Document with field rows (metadata schemas).
export const SchemaArt = () => (
  <svg {...svgProps} aria-hidden="true">
    <rect x="72" y="30" width="76" height="94" rx="8" fill="rgba(255,255,255,0.14)" stroke="rgba(255,255,255,0.30)" strokeWidth="1.2" />
    <rect x="86" y="46" width="48" height="7" rx="3.5" fill="rgba(255,255,255,0.5)" />
    <rect x="86" y="62" width="34" height="6" rx="3" fill="rgba(255,255,255,0.32)" />
    <rect x="86" y="78" width="42" height="6" rx="3" fill="rgba(255,255,255,0.32)" />
    <rect x="86" y="94" width="28" height="6" rx="3" fill="rgba(255,255,255,0.32)" />
    <circle cx="150" cy="52" r="4" fill="rgba(255,255,255,0.6)" />
    <Accents />
  </svg>
);

// Overlapping label tags with a check (content classification / labeling).
export const TagArt = () => (
  <svg {...svgProps} aria-hidden="true">
    {/* back tag */}
    <path d="M104 42 L168 42 L168 86 L104 86 L82 64 Z"
      fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.22)" strokeWidth="1.1" />
    <circle cx="102" cy="64" r="4" fill="rgba(255,255,255,0.14)" />
    {/* front tag */}
    <path d="M88 56 L152 56 L152 100 L88 100 L66 78 Z"
      fill="rgba(255,255,255,0.22)" stroke="rgba(255,255,255,0.42)" strokeWidth="1.3" />
    <circle cx="86" cy="78" r="4.5" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" />
    {/* label lines + check on the front tag */}
    <rect x="104" y="70" width="34" height="5" rx="2.5" fill="rgba(255,255,255,0.5)" />
    <polyline points="106,86 114,94 132,74"
      stroke="rgba(255,255,255,0.85)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <Accents />
  </svg>
);

// Folder + hierarchy tree (content organization: folders / taxonomy / content types).
export const FolderTreeArt = () => (
  <svg {...svgProps} aria-hidden="true">
    {/* folder */}
    <rect x="40" y="46" width="34" height="12" rx="3" fill="rgba(255,255,255,0.20)" />
    <rect x="34" y="54" width="82" height="56" rx="9" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.30)" strokeWidth="1.2" />
    <rect x="34" y="54" width="82" height="18" rx="9" fill="rgba(255,255,255,0.10)" />
    <rect x="50" y="80" width="50" height="4.5" rx="2.2" fill="rgba(255,255,255,0.26)" />
    <rect x="50" y="91" width="34" height="4.5" rx="2.2" fill="rgba(255,255,255,0.17)" />
    {/* hierarchy tree */}
    <line x1="116" y1="82" x2="132" y2="82" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />
    <rect x="132" y="74" width="16" height="16" rx="4" fill="rgba(255,255,255,0.30)" stroke="rgba(255,255,255,0.40)" strokeWidth="1" />
    <line x1="148" y1="82" x2="160" y2="82" stroke="rgba(255,255,255,0.20)" strokeWidth="1.2" />
    <line x1="160" y1="50" x2="160" y2="114" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" />
    <line x1="160" y1="50" x2="174" y2="50" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" />
    <line x1="160" y1="82" x2="174" y2="82" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" />
    <line x1="160" y1="114" x2="174" y2="114" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" />
    <rect x="174" y="43" width="14" height="14" rx="4" fill="rgba(255,255,255,0.24)" />
    <rect x="174" y="75" width="14" height="14" rx="4" fill="rgba(255,255,255,0.18)" />
    <rect x="174" y="107" width="14" height="14" rx="4" fill="rgba(255,255,255,0.13)" />
    <Accents />
  </svg>
);
